/**
 * Centralized branded types for PODx Core
 * Prevents circular dependencies and provides consistent type definitions
 */

// Core branded types
declare const UserIdBrand: unique symbol;
export type UserId = string & { readonly [UserIdBrand]: typeof UserIdBrand };

declare const TweetIdBrand: unique symbol;
export type TweetId = string & { readonly [TweetIdBrand]: typeof TweetIdBrand };

declare const SessionIdBrand: unique symbol;
export type SessionId = string & { readonly [SessionIdBrand]: typeof SessionIdBrand };

declare const RequestIdBrand: unique symbol;
export type RequestId = string & { readonly [RequestIdBrand]: typeof RequestIdBrand };

declare const EmailBrand: unique symbol;
export type Email = string & { readonly [EmailBrand]: typeof EmailBrand };

declare const UsernameBrand: unique symbol;
export type Username = string & { readonly [UsernameBrand]: typeof UsernameBrand };

declare const TokenBrand: unique symbol;
export type Token = string & { readonly [TokenBrand]: typeof TokenBrand };

declare const HashBrand: unique symbol;
export type Hash = string & { readonly [HashBrand]: typeof HashBrand };

declare const TimestampBrand: unique symbol;
export type Timestamp = number & { readonly [TimestampBrand]: typeof TimestampBrand };

declare const CountBrand: unique symbol;
export type Count = number & { readonly [CountBrand]: typeof CountBrand };

declare const PercentageBrand: unique symbol;
export type Percentage = number & { readonly [PercentageBrand]: typeof PercentageBrand };

// Debug-specific branded types
declare const DebugSessionIdBrand: unique symbol;
export type DebugSessionId = string & { readonly [DebugSessionIdBrand]: typeof DebugSessionIdBrand };

declare const PerformanceMetricIdBrand: unique symbol;
export type PerformanceMetricId = string & { readonly [PerformanceMetricIdBrand]: typeof PerformanceMetricIdBrand };

// Error handling branded types
declare const ResultIdBrand: unique symbol;
export type ResultId = string & { readonly [ResultIdBrand]: typeof ResultIdBrand };

declare const RetryAttemptBrand: unique symbol;
export type RetryAttempt = number & { readonly [RetryAttemptBrand]: typeof RetryAttemptBrand };

// Troubleshooting branded types
declare const HealthCheckIdBrand: unique symbol;
export type HealthCheckId = string & { readonly [HealthCheckIdBrand]: typeof HealthCheckIdBrand };

declare const DiagnosticIdBrand: unique symbol;
export type DiagnosticId = string & { readonly [DiagnosticIdBrand]: typeof DiagnosticIdBrand };

// Type guards for branded types
export const isUserId = (value: string): value is UserId => {
  return typeof value === 'string' && value.length > 0;
};

export const isTweetId = (value: string): value is TweetId => {
  return typeof value === 'string' && /^\d+$/.test(value);
};

export const isEmail = (value: string): value is Email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

export const isUsername = (value: string): value is Username => {
  return typeof value === 'string' && /^[a-zA-Z0-9_]{1,15}$/.test(value);
};

export const isToken = (value: string): value is Token => {
  return typeof value === 'string' && value.length >= 32;
};

export const isHash = (value: string): value is Hash => {
  return typeof value === 'string' && /^[a-f0-9]{32,}$/i.test(value);
};

export const isTimestamp = (value: number): value is Timestamp => {
  return typeof value === 'number' && value > 0 && value < Number.MAX_SAFE_INTEGER;
};

export const isCount = (value: number): value is Count => {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
};

export const isPercentage = (value: number): value is Percentage => {
  return typeof value === 'number' && value >= 0 && value <= 100;
};

// Branded type constructors with validation
export const createUserId = (value: string): UserId => {
  if (!isUserId(value)) {
    throw new Error(`Invalid UserId: ${value}`);
  }
  return value as UserId;
};

export const createTweetId = (value: string): TweetId => {
  if (!isTweetId(value)) {
    throw new Error(`Invalid TweetId: ${value}`);
  }
  return value as TweetId;
};

export const createEmail = (value: string): Email => {
  if (!isEmail(value)) {
    throw new Error(`Invalid Email: ${value}`);
  }
  return value as Email;
};

export const createUsername = (value: string): Username => {
  if (!isUsername(value)) {
    throw new Error(`Invalid Username: ${value}`);
  }
  return value as Username;
};

export const createToken = (value: string): Token => {
  if (!isToken(value)) {
    throw new Error(`Invalid Token: ${value}`);
  }
  return value as Token;
};

export const createHash = (value: string): Hash => {
  if (!isHash(value)) {
    throw new Error(`Invalid Hash: ${value}`);
  }
  return value as Hash;
};

export const createTimestamp = (value: number): Timestamp => {
  if (!isTimestamp(value)) {
    throw new Error(`Invalid Timestamp: ${value}`);
  }
  return value as Timestamp;
};

export const createCount = (value: number): Count => {
  if (!isCount(value)) {
    throw new Error(`Invalid Count: ${value}`);
  }
  return value as Count;
};

export const createPercentage = (value: number): Percentage => {
  if (!isPercentage(value)) {
    throw new Error(`Invalid Percentage: ${value}`);
  }
  return value as Percentage;
};

// Utility functions for generating IDs
export const generateId = (prefix: string): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const generateDebugSessionId = (): DebugSessionId => {
  return generateId('session') as DebugSessionId;
};

export const generatePerformanceMetricId = (): PerformanceMetricId => {
  return generateId('metric') as PerformanceMetricId;
};

export const generateResultId = (): ResultId => {
  return generateId('result') as ResultId;
};

export const generateHealthCheckId = (): HealthCheckId => {
  return generateId('health') as HealthCheckId;
};

export const generateDiagnosticId = (): DiagnosticId => {
  return generateId('diagnostic') as DiagnosticId;
};

export const generateRequestId = (): RequestId => {
  return generateId('req') as RequestId;
};
